"use client";

import { useState, useRef, useEffect } from "react";
import "./Contact.css";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaAddressBook,
  FaPhoneSquareAlt,
  FaBusinessTime,
  FaYoutube,
} from "react-icons/fa";

import { MdEmail } from "react-icons/md";

const Contact = () => {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    gstin: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Reset error state

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/contact-submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (response.ok) {
        console.log("Contact form submitted successfully:", data);
        setFormSubmitted(true);
        setTimeout(() => {
          setFormData({
            name: "",
            email: "",
            phone: "",
            company: "",
            message: "",
            gstin: "",
          });
          setFormSubmitted(false);
        }, 5000);
      } else {
        setError(data.error || "Failed to submit contact form");
      }
    } catch (err) {
      console.error("Error submitting contact form:", err);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = sectionRef.current.querySelectorAll(".animate-in");
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="contact" ref={sectionRef}>
      <div className="container">
        <h2 className="text-center animate-in">Contact Us</h2>
        <p className="contact-subtitle text-center animate-in">
          Get in touch with our team for inquiries, quotes, or support
        </p>

        <div className="contact-content">
          <div className="contact-info animate-in">
            <div className="contact-card">
              <h3>Contact Information</h3>
              <div className="contact-details">
                <div className="contact-detail">
                  <div className="contact-icon">
                    <FaAddressBook />
                  </div>
                  <div>
                    <h4>Address</h4>
                    <p>
                      11, Parmar Industrial Estate,
                      <br />
                      Behind Platinum Hospital & HP Petrol Pump,
                      <br />
                      Vasai East, Mumbai, Maharashtra 401208
                    </p>
                  </div>
                </div>
                <div className="contact-detail">
                  <div className="contact-icon">
                    <FaPhoneSquareAlt />
                  </div>
                  <div>
                    <h4>Phone</h4>
                    <p>
                      <a href="tel:+919822973407">+91 98229 73407</a>
                    </p>
                    <p>
                      <a href="tel:+919765053894">+91 97650 53894</a>
                    </p>
                  </div>
                </div>
                <div className="contact-detail">
                  <div className="contact-icon">
                    <MdEmail />
                  </div>
                  <div>
                    <h4>Email</h4>
                    <p>
                      <a href="mailto:info@abmmetaltech.com">
                        info@abmmetaltech.com
                      </a>
                    </p>
                    <p>
                      <a href="mailto:abmmetaltech@gmail.com">
                        abmmetaltech@gmail.com
                      </a>
                    </p>
                  </div>
                </div>
                <div className="contact-detail">
                  <div className="contact-icon">
                    <FaBusinessTime />
                  </div>
                  <div>
                    <h4>Business Hours</h4>
                    <p>Monday - Saturday: 8:30 AM - 7:00 PM</p>
                  </div>
                </div>
              </div>
              <div className="contact-social">
                <h4>Follow Us</h4>
                <div className="social-icons">
                  <a href="https://www.facebook.com/profile.php?id=61553381122486" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaFacebookF />
                  </a>
                  <a href="https://www.youtube.com/@ABMMetaltechIndia" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaYoutube />
                  </a>
                  <a href="https://www.linkedin.com/in/abmmetaltech/" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaLinkedinIn />
                  </a>
                  <a href="https://www.instagram.com/abm.metal.tech/" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaInstagram />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container animate-in">
            <div className="contact-form-card">
              <h3>Send Us a Message</h3>
              {formSubmitted ? (
                <div className="form-success">
                  <div className="success-icon"></div>
                  <h4>Thank you for your message!</h4>
                  <p>
                    We've received your inquiry and will get back to you
                    shortly.
                  </p>
                </div>
              ) : (
                <>
                  {error && <div className="error-message">{error}</div>}
                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Your Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="company">Company Name</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="gstin">GSTIN</label>
                      <input
                        type="text"
                        id="gstin"
                        name="gstin"
                        value={formData.gstin}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows="4"
                        value={formData.message}
                        onChange={handleChange}
                        required
                      ></textarea>
                    </div>
                    <button type="submit" className="btn">
                      Send Message
                    </button>
                  </form>
                </>
              )}
              <hr className="form-divider" />
              <div className="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3763.2096731791753!2d72.849802!3d19.403344600000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7af6fffffffff%3A0xac32866cab6b929a!2sABM%20Metal%20Tech!5e0!3m2!1sen!2sin!4v1745582509994!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ABM Metal Tech Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
